(() => {
  const { active, rate: rateString } = window.Shopify.currency;
  const rate = parseFloat(rateString) || 1;
  const format = (value) => new Intl.NumberFormat(document.documentElement.lang || 'en', {
    style: 'currency',
    currency: active,
    maximumFractionDigits: 0
  }).format(value);
  const bandWidth = (usd) => (usd < 1000 ? 50 : usd < 2500 ? 100 : usd < 5000 ? 250 : usd < 10000 ? 500 : usd < 25000 ? 1000 : usd < 50000 ? 2500 : usd < 100000 ? 5000 : usd < 250000 ? 50000 : 100000);
  const bandEnd = (usd) => usd + bandWidth(usd);

  class PriceRangeUsd extends HTMLElement {
    #abortController;

    connectedCallback() {
      this.#abortController = new AbortController();
      const signal = this.#abortController.signal;

      const boxes = Array.from(this.querySelectorAll('[data-usd]'));
      if (boxes.length === 0) return;
      const sentinel = this.querySelector('[data-usd-sentinel]');
      const rangeLower = this.querySelector('input[type="range"]:first-child');
      const rangeUpper = this.querySelector('input[type="range"]:last-child');
      const displayMin = this.querySelector('[data-display-min]');
      const displayMax = this.querySelector('[data-display-max]');
      const rangeGroup = this.querySelector('.range-group');

      const usdValues = boxes.map((b) => parseFloat(b.dataset.usd));
      const dispMax = Math.max(1, Math.ceil(bandEnd(Math.max(...usdValues)) * rate));
      [rangeLower, rangeUpper, displayMin, displayMax].forEach((el) => { el.max = dispMax; });
      displayMax.placeholder = dispMax;

      // The Prestige drawer moves this element to body on open and back on close. Keep the state across that reconnect.
      const saved = this.__state;
      sentinel.checked = saved ? saved.sentinel : new URLSearchParams(window.location.search).getAll(sentinel.name).includes('__none__');

      const active = boxes.filter((b) => b.checked).map((b) => parseFloat(b.dataset.usd));
      let usdMin = saved ? saved.usdMin : active.length ? Math.min(...active) : 0;
      let usdMaxExcl = saved ? saved.usdMaxExcl : active.length ? bandEnd(Math.max(...active)) : Infinity;
      const lo = usdMin > 0 ? Math.floor(usdMin * rate) : 0;
      const hi = usdMaxExcl < Infinity ? Math.min(dispMax, Math.ceil(usdMaxExcl * rate)) : dispMax;
      rangeLower.value = lo;
      rangeUpper.value = hi;
      displayMin.value = lo || '';
      displayMax.value = hi < dispMax ? hi : '';

      const updateProgress = () => {
        const min = parseInt(rangeLower.value);
        const max = parseInt(rangeUpper.value);
        rangeGroup.style.setProperty('--range-min', `${min / dispMax * 100}%`);
        rangeGroup.style.setProperty('--range-max', `${max / dispMax * 100}%`);
      };
      updateProgress();

      const setMin = (val) => { usdMin = val > 0 ? Math.floor(val / rate) : 0; };
      const setMax = (val) => { usdMaxExcl = val < dispMax ? Math.ceil(val / rate) + 1 : Infinity; };

      const commit = () => {
        const fullRange = usdMin <= 0 && usdMaxExcl === Infinity;
        let changed = false;
        boxes.forEach((box, i) => {
          const shouldCheck = !fullRange && usdValues[i] < usdMaxExcl && bandEnd(usdValues[i]) > usdMin;
          if (box.checked !== shouldCheck) { box.checked = shouldCheck; changed = true; }
        });
        const noneMatched = !fullRange && !boxes.some((b) => b.checked);
        if (sentinel.checked !== noneMatched) { sentinel.checked = noneMatched; changed = true; }
        this.__state = { usdMin, usdMaxExcl, sentinel: sentinel.checked };
        if (changed) boxes[0].dispatchEvent(new Event('change', { bubbles: true }));
      };

      displayMin.addEventListener('focus', () => displayMin.select(), { signal });
      displayMax.addEventListener('focus', () => displayMax.select(), { signal });

      displayMin.addEventListener('change', (event) => {
        event.stopPropagation();
        const val = Math.max(Math.min(parseInt(event.target.value) || 0, parseInt(displayMax.value || dispMax) - 1), 0);
        event.target.value = val || '';
        rangeLower.value = val;
        setMin(val);
        updateProgress();
        commit();
      }, { signal });

      displayMax.addEventListener('change', (event) => {
        event.stopPropagation();
        const val = Math.min(Math.max(parseInt(event.target.value) || dispMax, parseInt(displayMin.value || 0) + 1), dispMax);
        event.target.value = val < dispMax ? val : '';
        rangeUpper.value = val;
        setMax(val);
        updateProgress();
        commit();
      }, { signal });

      rangeLower.addEventListener('input', (event) => {
        const val = Math.min(parseInt(event.target.value), parseInt(rangeUpper.value) - 1);
        event.target.value = val;
        displayMin.value = val || '';
        updateProgress();
      }, { signal });

      rangeUpper.addEventListener('input', (event) => {
        const val = Math.max(parseInt(event.target.value), parseInt(rangeLower.value) + 1);
        event.target.value = val;
        displayMax.value = val < dispMax ? val : '';
        updateProgress();
      }, { signal });

      rangeLower.addEventListener('change', (event) => { event.stopPropagation(); setMin(parseInt(rangeLower.value)); commit(); }, { signal });
      rangeUpper.addEventListener('change', (event) => { event.stopPropagation(); setMax(parseInt(rangeUpper.value)); commit(); }, { signal });
    }

    disconnectedCallback() {
      this.#abortController.abort();
    }
  }

  class PriceChipUsd extends HTMLElement {
    connectedCallback() {
      const min = parseFloat(this.dataset.minUsd);
      const max = parseFloat(this.dataset.maxUsd);
      const label = this.dataset.label ? `${this.dataset.label}: ` : '';
      if (Number.isNaN(min) || Number.isNaN(max)) return;
      this.textContent = `${label}${format(Math.floor(min * rate))} - ${format(Math.ceil(bandEnd(max) * rate))}`;
    }
  }

  if (!window.customElements.get('price-range-usd')) window.customElements.define('price-range-usd', PriceRangeUsd);
  if (!window.customElements.get('price-chip-usd')) window.customElements.define('price-chip-usd', PriceChipUsd);
})();
